import {
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { useStore } from 'react-redux';

import type { StateWithSidekickChat } from '../state/chatTypes';
import {
  type EmitSidekickFeatureEvent,
  type SidekickFeatureEventReporter,
  SidekickFeatureEventsContext,
} from './SidekickFeatureEventsContext';
import type {
  SidekickFeatureEventCallback,
  SidekickFeatureEventInput,
} from './types';
import { readLatestQuestionId, watchQuestionSent } from './watchQuestionSent';

/** Props for {@link SidekickFeatureEventsProvider}. */
export type SidekickFeatureEventsProviderProps = {
  /** Host callback; omit it to report nothing. */
  onFeatureUsed?: SidekickFeatureEventCallback;
  children: ReactNode;
};

// The dock can be mounted without the chat slice, so a missing slice
// reads as no conversation rather than throwing.
const readActiveConversationId = (
  state: Partial<StateWithSidekickChat>
): string | null => state.aiSidekickChat?.conversationId ?? null;

/**
 * Hands the package's feature events to the host. The reporter keeps
 * one identity for the provider's lifetime, so memoized components
 * that report events do not re-render when the host passes a new
 * callback. A callback that throws is ignored, so tracking can never
 * break the chat.
 * @param props.onFeatureUsed - Receives each event.
 */
export const SidekickFeatureEventsProvider = ({
  onFeatureUsed,
  children,
}: SidekickFeatureEventsProviderProps): JSX.Element => {
  const store = useStore<Partial<StateWithSidekickChat>>();
  const onFeatureUsedRef = useRef(onFeatureUsed);
  const stopWatchingRef = useRef<(() => void) | null>(null);

  // A layout effect, so the callback is current before descendants'
  // passive effects report in the same commit.
  useLayoutEffect(() => {
    onFeatureUsedRef.current = onFeatureUsed;
  });

  useEffect(() => () => stopWatchingRef.current?.(), []);

  const deliver = useCallback(
    (
      callback: SidekickFeatureEventCallback | undefined,
      event: SidekickFeatureEventInput
    ) => {
      if (callback === undefined) return;
      try {
        callback({
          ...event,
          conversationId:
            event.conversationId || readActiveConversationId(store.getState()),
        });
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn(
            '[SidekickFeatureEventsProvider] host callback threw',
            error
          );
        }
      }
    },
    [store]
  );

  const emit = useCallback<EmitSidekickFeatureEvent>(
    (event) => deliver(onFeatureUsedRef.current, event),
    [deliver]
  );

  const captureEmit = useCallback(() => {
    const callback = onFeatureUsedRef.current;
    return (event: SidekickFeatureEventInput) => deliver(callback, event);
  }, [deliver]);

  const emitOnQuestionSent = useCallback<
    SidekickFeatureEventReporter['emitOnQuestionSent']
  >(
    (event, { question, submit }) => {
      const baselineQuestionId = readLatestQuestionId(store.getState());
      if (!submit()) return;
      stopWatchingRef.current?.();
      stopWatchingRef.current = watchQuestionSent({
        store,
        question,
        baselineQuestionId,
        onSent: () => emit(event),
      });
    },
    [emit, store]
  );

  const reporter = useMemo<SidekickFeatureEventReporter>(
    () => ({ emit, captureEmit, emitOnQuestionSent }),
    [captureEmit, emit, emitOnQuestionSent]
  );

  return (
    <SidekickFeatureEventsContext.Provider value={reporter}>
      {children}
    </SidekickFeatureEventsContext.Provider>
  );
};

export default SidekickFeatureEventsProvider;
